import { TextButtonWrapper } from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgenciesView.styled';

import { shiftModalRoutingService } from 'modals/ShiftModal/ShiftModal.ui.context';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { useFormBehaviors } from 'hooks';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useGQLMutation } from 'hooks/useGQLMutation.ts';
import { useTranslation } from 'hooks/useTranslation';
import {
  MUTATION_assignAgencyTeamMemberAssignOrder,
  MUTATION_assignMarketplaceOrder,
} from 'mutation';
import { createContext, FC, useContext } from 'react';
import { toastService } from 'services';
import {
  Mutation_AssignAgencyTeamMemberAssignOrderMutation as MUTATION_assignAgencyTeamMemberAssignOrder_type,
  Mutation_AssignAgencyTeamMemberAssignOrderMutationVariables,
  Mutation_AssignMarketplaceOrderMutation as MUTATION_assignMarketplaceOrder_type,
  Mutation_AssignMarketplaceOrderMutationVariables,
} from 'types/graphql';
import { VALIDATION_required } from 'validations';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';

export const AssignToAgencyTeamMemberFormContext = createContext<
  UseFormBehaviors<{ janeId: string; janeRelayId: string }>
>({} as UseFormBehaviors<{ janeId: string; janeRelayId: string }>);

export const AssignToAgencyTeamMemberFormProvider: FC = ({ children }) => {
  const { t } = useTranslation();
  const { data } = useShiftModalDataContext();
  const order = data?.order;
  const { refreshBackgroundPage } = useShiftModalPropsContext();

  const viewShift = () =>
    shiftModalRoutingService.open(
      {
        shiftModalShiftId: order.relayId,
      },
      'DetailView',
    );

  const SuccessToastMessage: FC = () => (
    <div>
      {t('ASSIGN_AGENCY_TEAM_MEMBER_SUCCESS_MSG')}&nbsp;
      <TextButtonWrapper variant="PurpleText" onClick={viewShift}>
        {t('schedule:VIEW_SHIFT')}
      </TextButtonWrapper>
    </div>
  );

  const { mutate: mutateAssignOrder } = useGQLMutation<
    MUTATION_assignAgencyTeamMemberAssignOrder_type,
    Mutation_AssignAgencyTeamMemberAssignOrderMutationVariables
  >({
    key: 'DELETE_TIME_OFF_VIEW_DELETE_TIME_OFF_BY_ID',
    mutation: MUTATION_assignAgencyTeamMemberAssignOrder,
    refetchQueries: ['scheduleTimeOffs'],
    onSuccess: (res) => {
      if (!!res.assignOrder.errors?.length) {
        // TODO: When tyler is done with making a more typed error object, fix this
        switch (res.assignOrder.errors[0]) {
          case 'this order can no longer be assigned':
            shiftModalRoutingService.goToView('ShiftUnavailableView');
            break;
          case "The order is outside the team member's schedule":
          case "The order conflicts with the team member's bookings":
            shiftModalRoutingService.goToView('TeamMemberUnavailableView');
            break;
        }
      } else {
        toastService.success(SuccessToastMessage, { autoClose: false });
        refreshBackgroundPage();
        shiftModalRoutingService.close();
      }
    },
    onError: handleGenericErrorMessage,
  });

  const { mutate: mutateAssignMarketplaceOrder } = useGQLMutation<
    MUTATION_assignMarketplaceOrder_type,
    Mutation_AssignMarketplaceOrderMutationVariables
  >({
    key: 'ASSIGN_MARKETPLACE_ORDER',
    mutation: MUTATION_assignMarketplaceOrder,
    refetchQueries: ['scheduleTimeOffs'],
    onSuccess: (res) => {
      if (!!res.assignMarketplaceOrder?.errors?.length) {
        switch (res.assignMarketplaceOrder.errors[0]) {
          case 'this order can no longer be assigned':
            shiftModalRoutingService.goToView('ShiftUnavailableView');
            break;
          case "The order is outside the team member's schedule":
          case "The order conflicts with the team member's bookings":
            shiftModalRoutingService.goToView('TeamMemberUnavailableView');
            break;
        }
      } else {
        toastService.success(SuccessToastMessage, { autoClose: false });
        refreshBackgroundPage();
        shiftModalRoutingService.close();
      }
    },
    onError: handleGenericErrorMessage,
  });

  const form = useFormBehaviors<{ janeId: string; janeRelayId: string }>({
    initialState: {
      janeId: '',
      janeRelayId: '',
    },
    validations: {
      janeId: VALIDATION_required,
      janeRelayId: VALIDATION_required,
    },
    onSubmit: (formValues) => {
      if (order.canB2bAssign) {
        mutateAssignMarketplaceOrder({
          variables: { orderId: `${order.relayId}`, janeId: formValues.janeRelayId },
        });
      } else {
        mutateAssignOrder({
          variables: { orderId: `${order.relayId}`, janeId: formValues.janeRelayId },
        });
      }
    },
    isDirtyCheckEnabled: true,
  });

  return (
    <AssignToAgencyTeamMemberFormContext.Provider value={form}>
      {children}
    </AssignToAgencyTeamMemberFormContext.Provider>
  );
};

export const useAssignToAgencyTeamMemberFormContext = (): UseFormBehaviors<{
  janeId: string;
  janeRelayId: string;
}> => useContext(AssignToAgencyTeamMemberFormContext);
